/**
 * Опрос точечного добора позиций судов по карточкам MarineTraffic.
 *
 * Тайловый проход (см. `fleetRefresh`) обновляет позиции широко и дёшево, но в
 * открытом океане MarineTraffic отдаёт большинство судов анонимно, и они
 * замирают на карте. Добор спрашивает карточку каждого нашего судна с
 * устаревшей позицией — это минуты работы вместо секунд.
 *
 * Отсюда главное отличие от `fleetRefresh`: подписчику сообщается КАЖДЫЙ опрос,
 * а не только готовность. Полоска под кнопкой рисует `processed/total`, а слой
 * флота перезапрашивается по ходу — суда переезжают по мере готовности. Ждать
 * конца и показать всё разом означало бы полторы минуты неподвижной карты.
 *
 * Опрос обязан заканчиваться при любом исходе: по готовности, по ошибке задания
 * и по таймауту. Сервис добора мог лежать — бесконечный опрос жёг бы запросы до
 * закрытия вкладки.
 */
import { formatTime, getLang, t } from '../i18n'

/** Период опроса. Совпадает с шагом полоски: чаще незачем, реже — рвано. */
export const CARD_POLL_MS = 5000

/** Через сколько сдаёмся: потолок 150 судов × ~1 с плюс запас на прогрев браузера.
 *
 *  Отсчитывается от СТАРТА задания, а не от нажатия. Воркер один, и до
 *  17.09.2026 эти 240 секунд уходили на ожидание чужой работы: разбор очереди —
 *  медиана ожидания 221 с, максимум 1276 с, — и человек видел «данные
 *  недоступны» при живом, просто ждущем задании. */
export const CARD_POLL_TIMEOUT_MS = 240_000

/** Сколько ждём задание в очереди. Столько же живёт само задание
 *  (`src/ais/mt_card_db.JOB_TTL_HOURS`): дольше его забракует воркер. */
export const CARD_QUEUE_TIMEOUT_MS = 60 * 60_000

/** Сколько держать итоговую строку после завершения, прежде чем убрать. */
export const CARD_RESULT_MS = 12_000

/**
 * Сколько неудачных опросов ПОДРЯД терпим, прежде чем бросить работу.
 *
 * Живой случай: бэкенд перезапустили ровно между двумя опросами (задание при
 * этом доработало до конца, 90 из 90), одиночный сбойный ответ обрывал опрос —
 * и под кнопкой навсегда оставалось «Поиск судов… 86 / 90». Рестарт занимает
 * секунды, а опрос идёт раз в пять: одной лишней попытки хватает с запасом.
 */
export const CARD_FETCH_ATTEMPTS = 3

/** Единственная фраза о неудаче добора. Совпадает по смыслу с серверной
 *  (`src/ais/mt_card_retry.UNAVAILABLE_MESSAGE`): к пользователю приходит факт
 *  недоступности, а не имя исключения. */
export const CARD_UNAVAILABLE_KEY = 'map.cards.unavailable'

export interface CardJobStatus {
  done: boolean
  status: string
  total: number
  processed: number
  updated: number
  failed_count: number
  error: string | null
  /** Воркер взял задание в работу. Нет поля — старый бэкенд: считаем начатым. */
  started?: boolean
  /** Сколько заданий воркер возьмёт раньше этого. */
  queue_ahead?: number
  /** Поштучный канал ShipFinder закрыт с этого момента: работали пачка и карточки. */
  pership_closed_since?: string | null
}

/** Взял ли воркер задание. Старый бэкенд полей очереди не отдаёт — тогда верим
 *  статусу, как верили до очереди. */
export function jobStarted(s: Pick<CardJobStatus, 'status' | 'started'>): boolean {
  return s.started ?? s.status !== 'pending'
}

/** Задание заменено более новым нажатием того же человека: полоска гаснет
 *  молча, её место заняла новая. */
export function isSuperseded(s: Pick<CardJobStatus, 'status'>): boolean {
  return s.status === 'superseded'
}

/** Состояние добора для экрана: то же, что приходит с сервера, плюс «работа началась». */
export interface CardProgress {
  processed: number
  total: number
  updated: number
  done: boolean
  error: string | null
  /** Ждёт в очереди: сколько заданий впереди. null/undefined — уже в работе. */
  queueAhead?: number | null
  /** Поштучный канал закрыт с этого момента (ISO) — итог пачкой и карточками. */
  perShipClosedSince?: string | null
}

/** Ответ сервера → состояние полоски. Одна функция на районный и точечный
 *  добор: разъехавшись, они показывали бы очередь по-разному. */
export function toCardProgress(s: CardJobStatus): CardProgress {
  return {
    processed: s.processed,
    total: s.total,
    updated: s.updated,
    done: s.done,
    error: s.error,
    queueAhead: jobStarted(s) ? null : (s.queue_ahead ?? 0),
    perShipClosedSince: s.pership_closed_since ?? null,
  }
}

/**
 * Заводить ли опрос по ответу постановки.
 *
 * Повтор того же кадра присоединяется к ОТКРЫТОМУ заданию: сервер отвечает тем
 * же id и `joined`. Если это задание уже опрашивается, второй опрос ничего не
 * добавит к полоске, а вдвое чаще спросит сервер. Опрос, успевший кончиться
 * (итог показан, сдались по таймауту), заводится заново.
 */
export function shouldStartCardPoll(
  resp: { card_job_id?: number | null; card_job_joined?: boolean },
  pollingJobId: number | null,
): boolean {
  const jobId = resp.card_job_id ?? null
  return !(resp.card_job_joined && jobId != null && pollingJobId === jobId)
}

/**
 * Подпись под полоской.
 *
 * ГЛАВНОЕ ЗДЕСЬ — НИКОГДА НЕ ИСЧЕЗАТЬ МОЛЧА. Первая же жалоба на живом проде
 * звучала как «позиции не обновились и прогресс-бар не виден», а на деле задание
 * отработало штатно: все суда района были проверены получасом раньше и попали
 * под трёхчасовой троттлинг. Работы не было — и экран не сказал об этом ничего,
 * что неотличимо от поломки.
 *
 * Поэтому у каждого исхода своя подпись: работа идёт, объём ещё считается,
 * обновлено столько-то, проверено, но свежее нет, проверять было нечего, ошибка.
 */
export function cardProgressLabel(p: CardProgress): string {
  // Технический текст наружу не выходит НИКОГДА. На карте однажды повисло
  // «Позиции судов: ('Connection aborted.', RemoteDisconnected(...))» — строка,
  // с которой пользователь не может сделать ничего. Обрыв связи с чужим сервисом
  // бэкенд теперь переигрывает сам (см. src/ais/mt_card_retry.py), и сюда ошибка
  // доходит, лишь когда попытки исчерпаны — то есть как факт недоступности.
  //
  // Но «сорвалось» и «ничего не пришло» — разные вещи, и путать их дорого.
  // Задание 100 на проде 09.08.2026 обновило 31 позицию из 39 опрошенных и
  // закрылось с ошибкой: кабинет посреди работы попросил проверку. Экран сказал
  // «данные недоступны» — при том что данные пришли, и почти по всем судам.
  // Молчать о недоделанном тоже нельзя: часть судов осталась неопрошенной.
  if (p.error) {
    return p.updated > 0
      ? t('map.cards.partial', { updated: p.updated })
      : t(CARD_UNAVAILABLE_KEY)
  }
  if (!p.done && p.queueAhead != null) {
    // Задание ждёт очереди. Сказать это обязательно: «Поиск судов…» над
    // неподвижной полоской минутами читается как зависание.
    return p.queueAhead > 0
      ? t('map.cards.queued', { count: p.queueAhead })
      : t('map.cards.queuedNext')
  }
  if (p.done && p.perShipClosedSince) {
    // Закрытый поштучный канал — штатный исход, а не отказ: пачка и карточки
    // отработали. «Обновлено 3 из 90» без объяснения читалось бы как поломка.
    return t('map.cards.perShipClosed', {
      since: formatTime(getLang(), p.perShipClosedSince),
      updated: p.updated,
    })
  }
  if (!p.done) {
    // Нейтрально: идёт поиск. Дробь добавляем, когда объём уже известен.
    return p.total > 0
      ? t('map.cards.searchingOf', { processed: p.processed, total: p.total })
      : t('map.cards.searching')
  }
  if (p.updated > 0) return t('map.cards.updated', { updated: p.updated, processed: p.processed })
  if (p.processed > 0) return t('map.cards.nothingNewer', { processed: p.processed })
  return t('map.cards.allFresh')
}

/**
 * Доля обработанных судов для полоски, 0..100.
 *
 * Округление ВВЕРХ: 1 судно из 150 — это 0,67%, и округление вниз оставило бы
 * полоску неподвижной на первых минутах работы. Нулевой объём (все суда района
 * проверены час назад) обязан давать 0, а не деление на ноль.
 */
export function cardProgressPercent({ processed, total }: { processed: number; total: number }) {
  if (!total) return 0
  return Math.min(100, Math.ceil((processed / total) * 100))
}

interface PollDeps {
  fetchStatus: (jobId: number) => Promise<CardJobStatus>
  onProgress: (status: CardJobStatus) => void
  sleep?: (ms: number) => Promise<void>
}

const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

export async function pollCardRefresh(
  jobId: number | null | undefined,
  { fetchStatus, onProgress, sleep = wait }: PollDeps,
): Promise<void> {
  if (!jobId) return

  let last: CardJobStatus | null = null
  let misses = 0

  /** Бросить опрос, СКАЗАВ об этом: замерший на середине счётчик неотличим от
   *  зависшего задания, и именно так его и прочитали. Дальше судьба задания нам
   *  неизвестна — отсюда нейтральная фраза о недоступности, а не выдуманный итог. */
  const giveUp = () => {
    onProgress({
      ...(last ?? { status: 'unknown', total: 0, processed: 0, updated: 0, failed_count: 0 }),
      done: true,
      error: 'poll-lost',
    })
  }

  // Два бюджета, и путать их нельзя: ожидание в очереди — не молчание воркера.
  const workPolls = Math.ceil(CARD_POLL_TIMEOUT_MS / CARD_POLL_MS)
  const queuePolls = Math.ceil(CARD_QUEUE_TIMEOUT_MS / CARD_POLL_MS)
  let worked = 0
  let queued = 0
  while (worked < workPolls && queued < queuePolls) {
    let status: CardJobStatus
    try {
      status = await fetchStatus(jobId)
    } catch {
      // Добор судов — побочная работа: её сетевой сбой не должен всплывать на
      // карте. Но и бросать с первого промаха нельзя — считаем промахи ПОДРЯД:
      // одиночные за две минуты работы не должны копиться и рвать живой опрос.
      if (++misses >= CARD_FETCH_ATTEMPTS) return giveUp()
      if (last && !jobStarted(last)) queued++
      else worked++
      await sleep(CARD_POLL_MS)
      continue
    }
    misses = 0
    last = status
    onProgress(status)
    if (status.done) return
    if (jobStarted(status)) worked++
    else queued++
    await sleep(CARD_POLL_MS)
  }
  // Время вышло, а задание всё идёт: смотреть дальше не будем, но и молчать
  // тоже — иначе полоска останется на экране до перезагрузки страницы.
  giveUp()
}
